import { useCallback, useState } from 'react'
import { useId } from '@react-aria/utils'
import { BatchCreateCampaignsModalProps } from 'screens/Campaigns/components/BatchCreateCampaignsModal/Types'
import { SelectNumberOfCampaigns } from 'screens/Campaigns/components/BatchCreateCampaignsModal/SelectNumberOfCampaigns/SelectNumberOfCampaigns'
import { SingleCampaign } from 'screens/Campaigns/components/BatchCreateCampaignsModal/SingleCampaign/SingleCampaign'
import { ModalLayoutProvider } from 'screens/Campaigns/components/BatchCreateCampaignsModal/ModalLayoutProvider/ModalLayoutProvider'
import { useConfirmModal } from 'hooks/useConfirmModal'
import { ConfirmBackModal } from 'flows/common/steps/BrandKitColorsAndFonts/ConfirmBackModal/ConfirmBackModal'
import { useModals } from 'almanac-ui/Modal'
import { BatchCampaigns } from 'screens/Campaigns/components/BatchCreateCampaignsModal/BatchCampaigns/BatchCampaigns'
import { BrandPlanStrategyType } from 'types'
import { AutopilotUpgradeEntryModal } from 'components/AutopilotUpgradeEntryModal/AutopilotUpgradeEntryModal'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { useRootStore } from 'stores/RootStore'
import { observer } from 'mobx-react-lite'

export const BatchCreateCampaignsModal = observer(({ startDate, brandPlan }: BatchCreateCampaignsModalProps) => {
  const id = useId()
  const { closeModal, openModal } = useModals()
  const { tierUsageStore } = useRootStore()
  const { currentSubscription } = tierUsageStore
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [selectedCampaigns, setSelectedCampaigns] = useState(1)
  const backConfirm = useConfirmModal(ConfirmBackModal)
  const isGrowthPlan = currentSubscription?.plan === PaymentPlanKey.BlazeAutopilotGrowth
  const handleClickOutside = async () => {
    const confirm = await backConfirm({
      title: 'Are you sure you want to close?',
      msg: 'All the progress will be lost.',
    })

    if (confirm) {
      closeModal()
    }
  }

  const handleSubmit = useCallback(() => {
    if (isGrowthPlan) {
      return setIsSubmitted(true)
    }

    openModal(AutopilotUpgradeEntryModal, { targetPlanKey: PaymentPlanKey.BlazeAutopilotGrowth })
  }, [isGrowthPlan, openModal])

  return (
    <ModalLayoutProvider
      size="sm"
      blaze
      aria-labelledby={id}
      onPressOutside={handleClickOutside}
      showFooter={isSubmitted}
    >
      <>
        {!isSubmitted && (
          <SelectNumberOfCampaigns value={selectedCampaigns} onChange={setSelectedCampaigns} onSubmit={handleSubmit} />
        )}
        {isSubmitted && selectedCampaigns === 1 && brandPlan.strategy_type === BrandPlanStrategyType.Crosspost && (
          <SingleCampaign startDate={startDate} brandPlan={brandPlan} onExit={() => setIsSubmitted(false)} />
        )}
        {isSubmitted && selectedCampaigns > 1 && (
          <BatchCampaigns
            brandPlan={brandPlan}
            numberOfCampaigns={selectedCampaigns}
            onExit={() => setIsSubmitted(false)}
          />
        )}
      </>
    </ModalLayoutProvider>
  )
})
